import { Dialog as RadixDialog } from "radix-ui";
import { clsx } from "clsx";
import { X } from "lucide-react";
import { twMerge } from "tailwind-merge";

export type DialogProps = Pick<
  RadixDialog.DialogProps,
  "open" | "onOpenChange"
> & {
  title: string;
  rootProps?: Omit<Partial<RadixDialog.DialogProps>, "open" | "onOpenChange">;
  contentProps?: Partial<RadixDialog.DialogContentProps>;
} & (
    | {
        children: React.ReactNode;
        description?: string | JSX.Element;
      }
    | {
        children?: React.ReactNode;
        description: string | JSX.Element;
      }
  );

export function Dialog({
  title,
  description,
  open,
  onOpenChange,
  children,
  rootProps,
  contentProps,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange} {...rootProps}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut z-overlay fixed inset-0 flex items-center justify-center bg-black/50 p-12">
          <RadixDialog.Content
            onPointerDownOutside={() => onOpenChange?.(false)}
            onEscapeKeyDown={() => onOpenChange?.(false)}
            {...contentProps}
            className={twMerge(
              clsx(
                "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut flex max-h-full w-125 flex-col rounded-md bg-white dark:bg-gray-100",
                contentProps?.className,
              ),
            )}
          >
            <div className="flex flex-none items-center justify-between border-b px-6 py-3">
              <RadixDialog.Title className="text-[18px] font-semibold">
                {title}
              </RadixDialog.Title>
              {onOpenChange && (
                <RadixDialog.Close
                  onClick={() => onOpenChange?.(false)}
                  className="hover:text-textDefault cursor-pointer p-0 text-gray-500 transition-colors"
                >
                  <X />
                </RadixDialog.Close>
              )}
            </div>

            <div className="overflow-auto p-6">
              {description && (
                <RadixDialog.Description asChild>
                  <div>{description}</div>
                </RadixDialog.Description>
              )}
              {!description && <RadixDialog.Description className="sr-only" />}
              {children}
            </div>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
interface DialogActionsWrapperProps {
  children: React.ReactNode;
  className?: string;
}
export function DialogActionsWrapper({
  children,
  className,
}: DialogActionsWrapperProps) {
  return (
    <div
      className={twMerge("mt-4 flex items-center justify-end gap-4", className)}
    >
      {children}
    </div>
  );
}
